import db from '../db/db'
import { AnalyticsEvents, OfficeEventParams } from '../types/AnalyticsEvents'
import Database from '../types/Database'
import { DrawingValidationResult, TldrawSnapshot } from '../types/DrawingTypes'
import { OfficeFormData } from '../types/ServiceTypes'

import { AnalyticsService } from './AnalyticsService'
import ErrorHandler from './ErrorHandler'

/**
 * OfficeService
 *
 * Service class for office-related business operations.
 * Handles office creation, validation, and retrieval with proper error handling.
 *
 * Requirements: 1.2, 1.3, 1.4, 2.1, 2.5, 8.1, 8.2, 8.5
 */
export class OfficeService {
  /**
   * Generate a unique ID for new offices
   */
  private static generateId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
      }
    )
  }

  /**
   * Create a new office
   *
   * Requirements:
   * - 1.2: Create office record in database
   * - 1.3: Assign creator as office owner
   * - 1.4: Generate unique office identifier
   * - 8.1: Validate user authentication
   * - 8.2: Sanitize input data
   * - 5.3, 5.4: Enhanced error handling with retry support
   */
  static async createOffice(
    officeData: OfficeFormData,
    user: Database.User
  ): Promise<Database.Office> {
    try {
      // Requirement 8.1: Validate user authentication
      if (!user.uid || user.uid === '') {
        const authError = new Error('Authentication required to create office')
        ErrorHandler.logPermissionError(
          'Authentication validation failed',
          { userId: user.uid },
          { component: 'OfficeService', action: 'createOffice' }
        )
        throw authError
      }

      // Requirement 8.2: Sanitize input data
      const sanitizedData = this.sanitizeOfficeData(officeData)

      // Validate office data
      const existingOffices = await this.getUserOffices(user.uid)
      const validationErrors = await this.validateOfficeData(
        sanitizedData,
        existingOffices
      )

      if (validationErrors.length > 0) {
        const validationError = new Error(
          `Validation failed: ${validationErrors.join(', ')}`
        )
        ErrorHandler.logValidationError(
          'Office data validation failed',
          { errors: validationErrors, officeData: sanitizedData },
          { component: 'OfficeService', action: 'createOffice' }
        )
        throw validationError
      }

      // Requirement 1.4: Generate unique office identifier
      const newOfficeId = this.generateId()
      const now = new Date()

      // Requirement 1.2: Create office record in database
      // Requirement 1.3: Assign creator as office owner
      // Requirement 1.5: Set default subscription tier and capacity
      const newOffice: Database.Office = {
        id: newOfficeId, // Set Firestore document ID to match oid
        name: sanitizedData.name,
        oid: newOfficeId,
        uid: user.uid, // Creator becomes owner
        description: sanitizedData.description,
        address: sanitizedData.address,
        contactInfo: sanitizedData.contactInfo,
        subscriptionTier: 'free', // Default to Free tier
        userCapacity: 5, // Free tier capacity
        drawingData: sanitizedData.drawingData || null, // Include drawing data if provided
        createdAt: now,
        updatedAt: now
      }

      ErrorHandler.logInfo(
        'Creating new office',
        {
          officeName: newOffice.name,
          officeId: newOfficeId,
          userId: user.uid
        },
        { component: 'OfficeService', action: 'createOffice' }
      )

      // Use transaction-like approach for data consistency
      try {
        await db.addItem('offices', newOffice)

        // Create default subscription record for Free tier
        const { createSubscription } = await import('../db/db')
        const subscriptionData: Omit<Database.Subscription, 'id'> = {
          officeId: newOfficeId,
          ownerId: user.uid,
          tier: 'free',
          status: 'active',
          billingInterval: 'monthly',
          currentPeriodStart: now,
          currentPeriodEnd: new Date(
            now.getFullYear() + 100,
            now.getMonth(),
            now.getDate()
          ), // Far future for free tier
          cancelAtPeriodEnd: false,
          createdAt: now,
          updatedAt: now
        }

        await createSubscription(subscriptionData)

        // Update user's office IDs
        const newOfficeIds = user.officeids?.slice() ?? []
        newOfficeIds.push(newOfficeId)

        if (user.id) {
          await db.updateItem('users', user.id, {
            officeids: newOfficeIds
          })
        }

        ErrorHandler.logInfo(
          'Office created successfully',
          { officeId: newOfficeId, officeName: newOffice.name },
          { component: 'OfficeService', action: 'createOffice' }
        )

        // Track office creation event
        const analyticsParams: OfficeEventParams = {
          subscription_tier: newOffice.subscriptionTier,
          is_manager: true // Creator is always a manager
        }
        AnalyticsService.trackEvent(
          AnalyticsEvents.OFFICE_CREATED,
          analyticsParams
        )

        return newOffice
      } catch (dbError) {
        // Handle database-specific errors
        const error =
          dbError instanceof Error ? dbError : new Error(String(dbError))

        if (error.message.includes('permission-denied')) {
          throw new Error('You do not have permission to create offices')
        }

        if (error.message.includes('quota-exceeded')) {
          throw new Error('Storage quota exceeded. Please contact support')
        }

        if (
          error.message.includes('unavailable') ||
          error.message.includes('deadline-exceeded')
        ) {
          throw new Error('Service temporarily unavailable. Please try again')
        }

        // Generic database error
        throw new Error('Failed to save office data. Please try again')
      }
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      // Don't wrap already handled errors
      if (
        errorInstance.message.includes('Validation failed:') ||
        errorInstance.message.includes('Authentication') ||
        errorInstance.message.includes('permission') ||
        errorInstance.message.includes('quota') ||
        errorInstance.message.includes('unavailable')
      ) {
        throw errorInstance
      }

      // Log and wrap unexpected errors
      ErrorHandler.logSystemError(
        'Unexpected error creating office',
        errorInstance,
        {
          component: 'OfficeService',
          action: 'createOffice'
        }
      )
      throw new Error(`Failed to create office: ${errorInstance.message}`)
    }
  }

  /**
   * Validate office data according to business rules
   *
   * Requirements:
   * - 2.1: Office name required with minimum 2 characters
   * - 2.5: Prevent duplicate office names per user
   */
  static async validateOfficeData(
    officeData: OfficeFormData,
    existingOffices: Database.Office[]
  ): Promise<string[]> {
    const errors: string[] = []

    // Requirement 2.1: Office name validation
    if (!officeData.name || officeData.name.trim() === '') {
      errors.push('Office name is required')
    } else if (officeData.name.trim().length < 2) {
      errors.push('Office name must be at least 2 characters long')
    } else if (officeData.name.length > 100) {
      errors.push('Office name cannot exceed 100 characters')
    }

    // Requirement 2.5: Prevent duplicate office names per user
    if (
      officeData.name &&
      existingOffices.some(
        office => office.name?.toLowerCase() === officeData.name.toLowerCase()
      )
    ) {
      errors.push('An office with this name already exists')
    }

    // Optional field validations
    if (officeData.description && officeData.description.length > 500) {
      errors.push('Office description cannot exceed 500 characters')
    }

    if (officeData.address && officeData.address.length > 200) {
      errors.push('Office address cannot exceed 200 characters')
    }

    if (officeData.contactInfo && officeData.contactInfo.length > 200) {
      errors.push('Office contact information cannot exceed 200 characters')
    }

    return errors
  }

  /**
   * Validate drawing data before saving to Firestore
   *
   * Requirements:
   * - 8.1: Validate JSON format and tldraw snapshot structure
   * - 8.2: Display warning if drawing exceeds 900KB
   * - 8.3: Prevent saving if drawing is corrupted or invalid
   * - 8.4: Check size limits (900KB warning, 1MB hard limit)
   *
   * @param drawingData - Serialized tldraw snapshot as JSON string
   * @returns DrawingValidationResult with validation status and size info
   */
  static validateDrawingData(drawingData: string): DrawingValidationResult {
    const WARNING_THRESHOLD = 900 * 1024 // 900KB in bytes
    const HARD_LIMIT = 1024 * 1024 // 1MB in bytes

    try {
      // Calculate size in bytes
      const sizeInBytes = new Blob([drawingData]).size

      // Check hard limit first
      if (sizeInBytes > HARD_LIMIT) {
        ErrorHandler.logValidationError(
          'Drawing data exceeds hard limit',
          { sizeInBytes, hardLimit: HARD_LIMIT },
          { component: 'OfficeService', action: 'validateDrawingData' }
        )
        return {
          isValid: false,
          error:
            'Drawing data exceeds 1MB limit. Please simplify your drawing.',
          sizeInBytes,
          exceedsWarningThreshold: true
        }
      }

      // Validate JSON format
      let parsedData: TldrawSnapshot
      try {
        parsedData = JSON.parse(drawingData) as TldrawSnapshot
      } catch (parseError) {
        ErrorHandler.logValidationError(
          'Drawing data is not valid JSON',
          { parseError },
          { component: 'OfficeService', action: 'validateDrawingData' }
        )
        return {
          isValid: false,
          error: 'Drawing data is corrupted or invalid.',
          sizeInBytes,
          exceedsWarningThreshold: sizeInBytes > WARNING_THRESHOLD
        }
      }

      // Validate tldraw snapshot structure
      // tldraw v2.x uses {document, session} structure
      // tldraw v4.x uses {store, schema} structure
      const hasV2Structure =
        parsedData.document && typeof parsedData.document === 'object'
      const hasV4Structure =
        parsedData.store && typeof parsedData.store === 'object'

      if (!hasV2Structure && !hasV4Structure) {
        ErrorHandler.logValidationError(
          'Drawing data missing required properties (needs document+session for v2.x or store for v4.x)',
          { parsedData, keys: Object.keys(parsedData) },
          { component: 'OfficeService', action: 'validateDrawingData' }
        )
        return {
          isValid: false,
          error: 'Drawing data structure is invalid.',
          sizeInBytes,
          exceedsWarningThreshold: sizeInBytes > WARNING_THRESHOLD
        }
      }

      // For v2.x, validate session exists
      if (
        hasV2Structure &&
        (!parsedData.session || typeof parsedData.session !== 'object')
      ) {
        ErrorHandler.logValidationError(
          'Drawing data missing required session property for v2.x',
          { parsedData },
          { component: 'OfficeService', action: 'validateDrawingData' }
        )
        return {
          isValid: false,
          error: 'Drawing data structure is invalid.',
          sizeInBytes,
          exceedsWarningThreshold: sizeInBytes > WARNING_THRESHOLD
        }
      }

      // Schema validation is optional for both versions
      if (parsedData.schema && typeof parsedData.schema !== 'object') {
        ErrorHandler.logValidationError(
          'Drawing data schema property has invalid type',
          { schema: parsedData.schema },
          { component: 'OfficeService', action: 'validateDrawingData' }
        )
        return {
          isValid: false,
          error: 'Drawing data structure is invalid.',
          sizeInBytes,
          exceedsWarningThreshold: sizeInBytes > WARNING_THRESHOLD
        }
      }

      // Check warning threshold
      const exceedsWarningThreshold = sizeInBytes > WARNING_THRESHOLD

      if (exceedsWarningThreshold) {
        ErrorHandler.logWarning(
          'Drawing data exceeds warning threshold',
          { sizeInBytes, warningThreshold: WARNING_THRESHOLD },
          { component: 'OfficeService', action: 'validateDrawingData' }
        )
      }

      // Validation passed
      ErrorHandler.logInfo(
        'Drawing data validation successful',
        { sizeInBytes, exceedsWarningThreshold },
        { component: 'OfficeService', action: 'validateDrawingData' }
      )

      return {
        isValid: true,
        sizeInBytes,
        exceedsWarningThreshold
      }
    } catch (error) {
      // Catch any unexpected errors
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError(
        'Unexpected error validating drawing data',
        errorInstance,
        { component: 'OfficeService', action: 'validateDrawingData' }
      )

      return {
        isValid: false,
        error: 'Failed to validate drawing data. Please try again.',
        sizeInBytes: 0,
        exceedsWarningThreshold: false
      }
    }
  }

  /**
   * Get all offices for a specific user
   *
   * Requirements:
   * - Retrieve offices owned by the user
   * - Handle network errors gracefully
   */
  static async getUserOffices(userId: string): Promise<Database.Office[]> {
    try {
      if (!userId) {
        ErrorHandler.logWarning(
          'getUserOffices called with empty userId',
          { userId },
          { component: 'OfficeService', action: 'getUserOffices' }
        )
        return []
      }

      ErrorHandler.logInfo(
        'Fetching user offices',
        { userId },
        { component: 'OfficeService', action: 'getUserOffices' }
      )

      const offices = await db.query({
        collection: 'offices',
        where: ['uid', '==', userId]
      })

      ErrorHandler.logInfo(
        'Successfully fetched user offices',
        { userId, officeCount: offices.length },
        { component: 'OfficeService', action: 'getUserOffices' }
      )

      return offices
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      // Handle specific network errors
      if (
        errorInstance.message.includes('Failed to fetch') ||
        errorInstance.message.includes('Network request failed')
      ) {
        ErrorHandler.logNetworkError(
          'Network connection failed while fetching offices',
          errorInstance,
          {
            component: 'OfficeService',
            action: 'getUserOffices'
          }
        )
        throw new Error(
          'Network connection lost. Please check your internet connection'
        )
      }

      if (errorInstance.message.includes('timeout')) {
        ErrorHandler.logNetworkError(
          'Request timeout while fetching offices',
          errorInstance,
          {
            component: 'OfficeService',
            action: 'getUserOffices'
          }
        )
        throw new Error('Request timed out. Please try again')
      }

      if (errorInstance.message.includes('permission-denied')) {
        ErrorHandler.logPermissionError(
          'Permission denied while fetching offices',
          errorInstance,
          {
            component: 'OfficeService',
            action: 'getUserOffices'
          }
        )
        throw new Error('You do not have permission to access office data')
      }

      // Generic error handling
      ErrorHandler.logSystemError('Error getting user offices', errorInstance, {
        component: 'OfficeService',
        action: 'getUserOffices'
      })
      throw new Error('Failed to load office data. Please try again')
    }
  }

  /**
   * Update an existing office
   *
   * Requirements:
   * - 3.2: Track office_updated event after successful update
   * - 3.4: Include subscription tier in event parameters
   * - 3.5: Include is_manager in event parameters
   */
  static async updateOffice(
    officeId: string,
    updates: Partial<OfficeFormData>,
    userId?: string
  ): Promise<void> {
    try {
      if (!officeId) {
        throw new Error('Office ID is required for update')
      }

      // Sanitize update data
      const sanitizedUpdates = this.sanitizeOfficeData(updates)

      const updateData: Partial<Database.Office> = {
        ...sanitizedUpdates,
        updatedAt: new Date()
      }

      ErrorHandler.logInfo(
        'Updating office',
        {
          officeId,
          updateData
        },
        { component: 'OfficeService', action: 'updateOffice' }
      )

      await db.updateItem('offices', officeId, updateData)

      // Track office update event
      if (userId) {
        try {
          // Get the updated office to retrieve subscription tier
          const office = await db.getById('offices', officeId)

          if (office) {
            // Check if user is a manager (owner or in officeManagers collection)
            const isOwner = office.uid === userId
            const managerRecords = await db.query({
              collection: 'officeManagers',
              where: ['officeId', '==', officeId]
            })
            const isManager =
              isOwner ||
              managerRecords.some(
                (manager: Database.OfficeManager) => manager.userId === userId
              )

            const analyticsParams: OfficeEventParams = {
              subscription_tier: office.subscriptionTier,
              is_manager: isManager
            }
            AnalyticsService.trackEvent(
              AnalyticsEvents.OFFICE_UPDATED,
              analyticsParams
            )
          }
        } catch (analyticsError) {
          // Log analytics error but don't fail the update operation
          ErrorHandler.logWarning(
            'Failed to track office update event',
            { error: analyticsError },
            { component: 'OfficeService', action: 'updateOffice' }
          )
        }
      }
    } catch (error) {
      ErrorHandler.logSystemError('Error updating office', error, {
        component: 'OfficeService',
        action: 'updateOffice'
      })
      throw new Error(`Failed to update office: ${error}`)
    }
  }

  /**
   * Soft-delete an office by setting the isDeleted flag
   *
   * This marks the office as deleted without actually removing data.
   * The office will be hidden from all non-admin areas but remains
   * accessible in the admin section for restore operations.
   *
   * @param officeId - The office ID to soft-delete
   * @param userId - The user performing the soft-delete (must be owner or manager)
   */
  static async softDeleteOffice(officeId: string, userId: string): Promise<void> {
    try {
      if (!officeId) {
        throw new Error('Office ID is required for deletion')
      }

      if (!userId) {
        throw new Error('User ID is required for deletion')
      }

      ErrorHandler.logInfo(
        'Soft-deleting office',
        { officeId, userId },
        { component: 'OfficeService', action: 'softDeleteOffice' }
      )

      // Get office data to verify it exists and check permissions
      const office = await db.getById('offices', officeId)

      if (!office) {
        throw new Error('Office not found')
      }

      // Check if user is owner or manager
      const isOwner = office.uid === userId
      let isManager = false

      if (!isOwner) {
        const managerRecords = await db.query({
          collection: 'officeManagers',
          where: ['officeId', '==', officeId]
        })
        isManager = managerRecords.some(
          (manager: Database.OfficeManager) => manager.userId === userId
        )
      }

      if (!isOwner && !isManager) {
        throw new Error('Only office owners or managers can delete an office')
      }

      // Set the soft-delete flag
      await db.updateItem('offices', officeId, {
        isDeleted: true,
        deletedAt: new Date(),
        deletedBy: userId,
        updatedAt: new Date()
      })

      ErrorHandler.logInfo(
        'Successfully soft-deleted office',
        { officeId, userId },
        { component: 'OfficeService', action: 'softDeleteOffice' }
      )

      // Track office deletion event
      try {
        const analyticsParams: OfficeEventParams = {
          subscription_tier: office.subscriptionTier,
          is_manager: isOwner || isManager
        }
        AnalyticsService.trackEvent(
          AnalyticsEvents.OFFICE_DELETED,
          analyticsParams
        )
      } catch (analyticsError) {
        ErrorHandler.logWarning(
          'Failed to track office soft-deletion event',
          { error: analyticsError },
          { component: 'OfficeService', action: 'softDeleteOffice' }
        )
      }
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      if (
        errorInstance.message.includes('Office not found') ||
        errorInstance.message.includes('Only office owners') ||
        errorInstance.message.includes('Office ID is required') ||
        errorInstance.message.includes('User ID is required')
      ) {
        throw errorInstance
      }

      ErrorHandler.logSystemError('Error soft-deleting office', error, {
        component: 'OfficeService',
        action: 'softDeleteOffice'
      })
      throw new Error(`Failed to delete office: ${errorInstance.message}`)
    }
  }

  /**
   * Delete an office
   *
   * Requirements:
   * - 3.3: Track office_deleted event after successful deletion
   * - 3.4: Include subscription tier in event parameters
   * - 3.5: Include is_manager in event parameters
   */
  static async deleteOffice(officeId: string, userId: string): Promise<void> {
    try {
      if (!officeId) {
        throw new Error('Office ID is required for deletion')
      }

      if (!userId) {
        throw new Error('User ID is required for deletion')
      }

      ErrorHandler.logInfo(
        'Deleting office',
        { officeId, userId },
        { component: 'OfficeService', action: 'deleteOffice' }
      )

      // Get office data before deletion for analytics
      const office = await db.getById('offices', officeId)

      if (!office) {
        throw new Error('Office not found')
      }

      // Check if user is a manager (owner or in officeManagers collection)
      const isOwner = office.uid === userId
      const managerRecords = await db.query({
        collection: 'officeManagers',
        where: ['officeId', '==', officeId]
      })
      const isManager =
        isOwner ||
        managerRecords.some(
          (manager: Database.OfficeManager) => manager.userId === userId
        )

      // Delete the office
      await db.deleteItem('offices', officeId)

      ErrorHandler.logInfo(
        'Successfully deleted office',
        { officeId },
        { component: 'OfficeService', action: 'deleteOffice' }
      )

      // Track office deletion event
      try {
        const analyticsParams: OfficeEventParams = {
          subscription_tier: office.subscriptionTier,
          is_manager: isManager
        }
        AnalyticsService.trackEvent(
          AnalyticsEvents.OFFICE_DELETED,
          analyticsParams
        )
      } catch (analyticsError) {
        // Log analytics error but don't fail the delete operation
        ErrorHandler.logWarning(
          'Failed to track office deletion event',
          { error: analyticsError },
          { component: 'OfficeService', action: 'deleteOffice' }
        )
      }
    } catch (error) {
      ErrorHandler.logSystemError('Error deleting office', error, {
        component: 'OfficeService',
        action: 'deleteOffice'
      })
      throw new Error(`Failed to delete office: ${error}`)
    }
  }

  /**
   * Sanitize office data to prevent security vulnerabilities
   *
   * Requirement 8.2: Input sanitization
   */
  private static sanitizeOfficeData(data: OfficeFormData): OfficeFormData
  private static sanitizeOfficeData(
    data: Partial<OfficeFormData>
  ): Partial<OfficeFormData>
  private static sanitizeOfficeData(
    data: OfficeFormData | Partial<OfficeFormData>
  ): OfficeFormData | Partial<OfficeFormData> {
    const sanitized: Partial<OfficeFormData> = {}

    if (data.name !== undefined) {
      sanitized.name = data.name.trim()
    }

    if (data.description !== undefined) {
      sanitized.description = data.description.trim()
    }

    if (data.address !== undefined) {
      sanitized.address = data.address.trim()
    }

    if (data.contactInfo !== undefined) {
      sanitized.contactInfo = data.contactInfo.trim()
    }

    if (data.drawingData !== undefined) {
      // Drawing data is already a JSON string, no trimming needed
      // Just pass it through or set to null if empty
      sanitized.drawingData = data.drawingData || null
    }

    return sanitized as OfficeFormData | Partial<OfficeFormData>
  }
}

export default OfficeService
